import React from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { AddButton, RemoveButton } from './formParts';

/**
 * What a Python step PASSES ON, apart from what it decides.
 *
 * The outcome (the Output tab) is what the edges branch on. Context is
 * what a later step may read — `{{steps.gather.excerpts}}` in a judge
 * prompt, `t.steps.gather.excerpts` in Python. Declared here, returned as
 * the second value of evaluate(): `return ok, {"excerpts": [...]}`. The
 * runtime holds the return to this list the way it holds the verdict.
 *
 * A name can be used once: a key that is also an outcome field is refused
 * at save, so the two never shadow each other downstream.
 */

export const CONTEXT_TYPES = [
  { value: 'text', label: 'text' },
  { value: 'list', label: 'list' },
  { value: 'number', label: 'number' },
  { value: 'boolean', label: 'true / false' },
  { value: 'json', label: 'json' },
];

const input = 'h-7 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]';

export default function ContextFieldsEditor({ value, onChange, readOnly = false, taken = [] }) {
  const entries = Object.entries(value || {});
  const write = (next) => onChange(next.length ? Object.fromEntries(next) : undefined);
  const setName = (i, name) => write(entries.map((e, j) => (j === i ? [name, e[1]] : e)));
  const setType = (i, type) => write(entries.map((e, j) => (j === i ? [e[0], { type }] : e)));
  const add = () => {
    const used = new Set([...entries.map(([n]) => n), ...taken]);
    let name = 'excerpts'; let k = 2;
    while (used.has(name)) { name = `context_${k}`; k += 1; }
    write([...entries, [name, { type: 'list' }]]);
  };

  return (
    <div className="space-y-1.5" data-testid="context-fields">
      {entries.map(([name, spec], i) => {
        const clash = taken.includes(name);
        return (
          <div key={i} className="flex items-center gap-1.5">
            <input value={name} disabled={readOnly} aria-label={`Context ${i + 1} name`}
              title={clash ? `"${name}" is already an outcome field. A step decides with one name and passes on another` : undefined}
              onChange={(e) => setName(i, e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              className={`${input} w-[46%] font-mono ${clash ? 'border-[color:var(--feedback-error)]' : ''}`} />
            <div className="w-[34%]"><BrandDropdown compact ariaLabel={`Context ${i + 1} type`} value={spec.type} disabled={readOnly} onChange={(t) => setType(i, t)} options={CONTEXT_TYPES} /></div>
            <div className="flex-1" />
            {!readOnly && <RemoveButton label={`Remove context ${i + 1}`} onClick={() => write(entries.filter((_, j) => j !== i))} />}
          </div>
        );
      })}
      {!readOnly && (
        <AddButton onClick={add}>{entries.length ? 'Add' : 'Pass something on'}</AddButton>
      )}
    </div>
  );
}
